import { Avatar, Box, Typography } from '@mui/material'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import React, { useMemo, useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ProjectContributionFilterType } from '../../FilterMenu/Projects/FilterProjectContributionMenu'
import { EmployeeDetails, ProjectDetails } from '../../../store/redux/reducers/projectsSlice'
import { Order } from '../../../store/types/components/table/order'
import CollapsibleTable from '../CollapsibleTable/CollapsibleTable'
import { CollapsableRow, Row } from '../../../store/types/utils/DataTableUtils'
import TransitionTableCell from '../CollapsibleTable/TransitionTableCell'
import { useSelector } from 'react-redux'
import { getCurrentMode } from '../../../store/redux/reducers/darkModeSlice'
import { environment } from '../../../enviroments/enviroment'
import { getComparator, stableSort } from '../../../store/utils/sortingFunctions'
import { formatStringDate } from '../../../store/utils/dateUtils'
import { roundUpToNearestMinute } from '../../../store/utils/worklogUtils'
import { StyledFooterCell } from '../../../styles/WorklogTableStyles'

const ProjectDetailsTable = ({
  selectedFilters,
  searchInput,
  projectDetails,
  isNotLeaderOfProject,
}: {
  selectedFilters: ProjectContributionFilterType
  searchInput: string
  projectDetails: ProjectDetails
  isNotLeaderOfProject: boolean
}) => {
  const { t } = useTranslation()
  const [orderBy, setOrderBy] = useState<string>('')
  const [order, setOrder] = useState<Order>('asc')
  const [nestedOrderBy, setNestedOrderBy] = useState('')
  const mode = useSelector(getCurrentMode)
  const lastColumnRef = useRef<HTMLTableCellElement>(null)
  const [lastColumnWidth, setLastColumnWidth] = useState<number>(100)

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string, nestedProperty?: string) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)

    if (nestedProperty) {
      setNestedOrderBy(nestedProperty)
    } else {
      setNestedOrderBy('')
    }
  }

  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    const nestedProperties = property.split('.')
    if (nestedProperties.length === 2) {
      handleRequestSort(event, nestedProperties[0], nestedProperties[1])
    } else {
      handleRequestSort(event, property)
    }
  }

  const filterRowBySearchInput = (row: EmployeeDetails, searchInput: string) => {
    const searchTerm = searchInput.toLowerCase()
    const { employee, sumHours } = row

    return (
      employee.firstName.toLowerCase().includes(searchTerm) ||
      employee.lastName.toLowerCase().includes(searchTerm) ||
      sumHours.toString().includes(searchTerm)
    )
  }

  const filterRowByWorkTimeRange = (row: EmployeeDetails, workTimeRange: number | number[]) => {
    const { sumHours } = row

    if (Array.isArray(workTimeRange) && workTimeRange.length > 0) {
      const [minHours, maxHours] = workTimeRange
      return sumHours >= minHours && sumHours <= maxHours
    }

    return true
  }

  const filteredRows = useMemo(
    () =>
      stableSort(
        projectDetails.details.filter(row => {
          if (selectedFilters.employees.length > 0) {
            return (
              selectedFilters.employees.some(selectedEmployee => row.employee.id === selectedEmployee.id) &&
              filterRowBySearchInput(row, searchInput) &&
              filterRowByWorkTimeRange(row, selectedFilters.workTimeRange)
            )
          } else {
            return (
              filterRowBySearchInput(row, searchInput) && filterRowByWorkTimeRange(row, selectedFilters.workTimeRange)
            )
          }
        }),
        getComparator(order, orderBy as keyof EmployeeDetails, nestedOrderBy)
      ).slice(),
    [order, orderBy, projectDetails, searchInput, selectedFilters, nestedOrderBy]
  )

  const totalHours = useMemo(() => {
    if (filteredRows && filteredRows.length > 0) {
      return filteredRows.reduce((total, employee) => total + employee.sumHours, 0)
    } else {
      return 0
    }
  }, [filteredRows])

  const columns = [
    { id: 'employee.firstName+lastName', label: t('page.projects.detailsView.employee') },
    { id: 'sumHours', label: t('page.projects.detailsView.workTime') },
  ]

  const collapsableRows: CollapsableRow[] = filteredRows.map((row, index) => ({
    ...row,
    index: index,
    id: row.employee.id,
    hasCollapsedData: true,
    collapsedData:
      row.schedule && row.schedule.length > 0
        ? [...row.schedule]
            .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
            .map((value, index2) => ({ id: index2.toString(), ...value }))
        : [],
  }))

  const renderRow = (row: CollapsableRow) => {
    const DefaultProfileImage =
      mode === 'dark'
        ? `${environment.publicUrl}/assets/image/profile-default-dark.jpg`
        : `${environment.publicUrl}/assets/image/profile-default-light.jpg`
    const getSrc = () => {
      if (row.employee !== null && row.employee.avatar !== null && row.employee.extension !== 'LINK') {
        return 'data:image/png;base64,' + row.employee.avatar
      } else if (row.employee !== null && row.employee.avatar !== null && row.employee.extension === 'LINK') {
        return atob(row.employee.avatar !== undefined ? row.employee.avatar : '')
      } else {
        return DefaultProfileImage
      }
    }

    const src = getSrc()

    return (
      <>
        <TableCell align="left" sx={{ pl: 0 }}>
          <Box display={'flex'} gap={'4px'} alignItems={'center'}>
            <Avatar alt={row.employee.firstName} src={src} sx={{ width: 25, height: 25 }} />
            {row.employee.firstName + ' ' + row.employee.lastName}
          </Box>
        </TableCell>
        <TableCell align="center" ref={row.index === 0 ? lastColumnRef : null}>
          {`${roundUpToNearestMinute(row.sumHours)}h`}
        </TableCell>
      </>
    )
  }

  const renderCollapsedRow = (row: Row, open: boolean) => {
    return (
      <>
        <TransitionTableCell sx={{ padding: '0px', minWidth: '48px' }} open={open} />
        <TransitionTableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', pl: 0 }} open={open}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              lineHeight: 'inherit',
            }}
          >
            <Typography fontWeight={'bold'} sx={{ lineHeight: 'inherit' }}>
              {formatStringDate(row.date)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                inlineSize: 'auto',
                overflowWrap: 'break-word',
                lineHeight: 'inherit',
                maxWidth: '300px',
              }}
            >
              {row.description}
            </Typography>
          </Box>
        </TransitionTableCell>
        <TransitionTableCell align="center" open={open}>
          <Typography variant="body2" fontWeight={'bold'} sx={{ lineHeight: 'inherit' }}>
            {`${roundUpToNearestMinute(row.hours)}h`}
          </Typography>
        </TransitionTableCell>
      </>
    )
  }

  useEffect(() => {
    if (lastColumnRef.current) {
      setLastColumnWidth(lastColumnRef.current.offsetWidth)
    }
  }, [collapsableRows])

  const renderFooter = () => {
    return (
      <TableRow
        sx={{
          backgroundColor: 'background.main',
          zIndex: 2,
          height: 67,
        }}
      >
        <StyledFooterCell colSpan={2} sx={{ py: 1 }}>
          <Box sx={{ p: 1, pl: 3, pr: 5 }} display={'flex'} justifyContent={'space-between'}>
            <Typography sx={{ whiteSpace: 'break-spaces', fontWeight: 700 }}>
              {t('page.projects.detailsView.totalHoursInInterval')}
            </Typography>
          </Box>
        </StyledFooterCell>
        <StyledFooterCell
          align="center"
          sx={{
            py: 1,
            width: lastColumnWidth,
          }}
        >
          <Typography fontWeight={700}>{`${roundUpToNearestMinute(totalHours) || 0}h`}</Typography>
        </StyledFooterCell>
      </TableRow>
    )
  }

  return (
    <CollapsibleTable
      columns={columns}
      renderRow={renderRow}
      renderCollapsedRow={renderCollapsedRow}
      collapsableRows={collapsableRows}
      renderFooter={renderFooter}
      sortFunction={createSortHandler}
      sortFields={{ order, orderBy, nestedOrderBy }}
      noRowsText={
        isNotLeaderOfProject
          ? t('page.projects.detailsView.isNotLeaderOfProject')
          : t('page.projects.detailsView.noEmployeeContribution')
      }
      tableContainerSxProps={{
        maxHeight: 700,
        scrollbarWidth: 'auto',
        '&::-webkit-scrollbar': { display: 'block' },
        '@supports (-moz-appearance:none)': { scrollbarWidth: 'auto' },
      }}
    />
  )
}

export default ProjectDetailsTable
